// BETA 15.34 — Full pick audit log.
//
// Per Dylan: "we need to be watching what buffs all these things are giving
// to see if it is overpowered and creating god mode characters... so a full
// audit every bot run need to be added for that in supa base."
//
// Captures EVERY level-up perc / chest item / shrine buff / weapon level-up
// the player picks during a run, including:
//   - timestamp (gameTime)
//   - source ('levelup_perc' | 'chest_free' | 'chest_paid' | 'shrine' | 'levelup_weapon')
//   - pickedId / pickedName / pickedRarity / pickedValue
//   - offeredAlternatives (the 2 cards that were skipped, when applicable)
//   - playerStatsBefore / playerStatsAfter (so we can see "did this pick
//     turn the run god-mode")
//
// Reset in startNewRun. Snapshotted at finalizeRun via getPickAuditJson() +
// summary helpers. Summary columns flow to Supabase; full JSON goes to local
// CSV (and Supabase if it fits — JSON kept short by storing only key stats).

export type PickSource =
  | 'levelup_perc'
  | 'levelup_weapon'
  | 'shrine'
  | 'chest_free'
  | 'chest_paid';

export interface OfferedItem {
  id: string;
  name?: string;
  rarity?: string;       // 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'
  value?: number;        // the rolled buff amount (% or flat unit)
  isPercent?: boolean;
}

export interface PlayerStatsSnapshot {
  level: number;
  hp: number;
  maxHp: number;
  damageBonus: number;
  attackSpeedBonus: number;
  critChance: number;
  critDamage: number;
  luck: number;
  armor: number;
  goldHeld: number;
}

export interface PickAuditEntry {
  t: number;                                 // gameTime in seconds
  source: PickSource;
  pickedId: string;
  pickedName?: string;
  pickedRarity?: string;
  pickedValue?: number;
  pickedIsPercent?: boolean;
  offered?: OfferedItem[];                   // all cards offered (for offers we capture; optional)
  before: PlayerStatsSnapshot;
  // 'after' is populated 1 frame later by the engine update loop
  // — we only have the BEFORE stats at pick-time because the apply happens
  // after the callback. Leave undefined and let analyst compare across rows.
}

const _log: PickAuditEntry[] = [];

/** Reset the audit log — called once at startNewRun. */
export function resetPickAudit(): void {
  _log.length = 0;
}

/** Append an entry to the audit log. Best-effort, never throws. */
export function recordPick(entry: PickAuditEntry): void {
  try {
    _log.push(entry);
  } catch { /* never let audit logging crash the game */ }
}

/** Get a copy of the audit log (for finalizeRun snapshot). */
export function getPickAuditEntries(): PickAuditEntry[] {
  return [..._log];
}

/** Stringify the audit log for CSV / Supabase. Keeps each entry compact:
 *  ~150 bytes × ~20-50 picks per run = ~3-7KB total. Well below the 100KB
 *  truncation cap on Supabase. */
export function getPickAuditJson(): string {
  try {
    return JSON.stringify(_log);
  } catch {
    return '[]';
  }
}

// ─── Summary helpers (flat columns for Supabase fast-query) ────────────

/** Total number of pick events this run. */
export function getPickAuditCount(): number {
  return _log.length;
}

/** Count picks by source (returns object). Useful for "how many shrines vs
 *  chests" per run quickly without parsing the JSON. */
export function getPickAuditCountsBySource(): Record<PickSource, number> {
  const out: Record<PickSource, number> = {
    levelup_perc: 0,
    levelup_weapon: 0,
    shrine: 0,
    chest_free: 0,
    chest_paid: 0,
  };
  for (const e of _log) out[e.source] = (out[e.source] ?? 0) + 1;
  return out;
}

/** The single largest buff value the player picked this run.
 *  Returns { value, source, id, t } or null if no values logged. */
export function getMaxBuffSpike(): { value: number; source: PickSource; id: string; rarity?: string; t: number } | null {
  let best: { value: number; source: PickSource; id: string; rarity?: string; t: number } | null = null;
  for (const e of _log) {
    if (typeof e.pickedValue !== 'number') continue;
    if (!Number.isFinite(e.pickedValue)) continue;
    const v = Math.abs(e.pickedValue);
    if (!best || v > best.value) {
      best = { value: v, source: e.source, id: e.pickedId, rarity: e.pickedRarity, t: e.t };
    }
  }
  return best;
}

/** Count of legendary picks across all sources. */
export function getLegendaryPickCount(): number {
  return _log.filter(e => e.pickedRarity === 'legendary').length;
}

/** Count of epic+ picks (epic OR legendary). */
export function getEpicPlusPickCount(): number {
  return _log.filter(e => e.pickedRarity === 'epic' || e.pickedRarity === 'legendary').length;
}

// ─── Helper for hookers: snapshot player stats from a state object ──────

/** Build a stats snapshot from the live engine player. Used at every pick
 *  hook so the audit captures BEFORE-pick stats. The pickAudit module
 *  doesn't import GameState/Player to stay decoupled — caller passes the
 *  fields directly. */
export function snapshotStats(p: {
  level?: number; hp?: number; maxHp?: number;
  damageBonus?: number; attackSpeedBonus?: number;
  critChance?: number; critDamage?: number;
  luck?: number; armor?: number; gold?: number;
}): PlayerStatsSnapshot {
  return {
    level:            p.level ?? 1,
    hp:               Math.round(p.hp ?? 0),
    maxHp:            Math.round(p.maxHp ?? 100),
    damageBonus:      Math.round(p.damageBonus ?? 0),
    attackSpeedBonus: Math.round(p.attackSpeedBonus ?? 0),
    critChance:       Math.round(p.critChance ?? 0),
    critDamage:       Math.round(p.critDamage ?? 0),
    luck:             Math.round(p.luck ?? 0),
    armor:            Math.round(p.armor ?? 0),
    goldHeld:         Math.round(p.gold ?? 0),
  };
}
